import { Alert, Anchor, Code, Image, List, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconBulb } from '@tabler/icons-react';
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';
import { barve } from '../tema';
import type { Clanek } from './clanki';
import type { Blok, Vrstica } from './markdown';

/** Pot do slik člankov ({@code public/slike-pomoci/}). */
export const SLIKE_POMOCI = '/slike-pomoci/';

/**
 * Izris članka pomoči. {@code osnova}: pot za povezave »clanek:id« ({@code /pomoc} v aplikaciji, {@code /navodila}
 * javno); povezave »/…« so zasloni aplikacije, »http…« se odprejo v novem zavihku.
 */
export function ClanekVsebina({
  clanek,
  osnova,
  brezNaslova = false,
}: {
  clanek: Clanek;
  osnova: string;
  brezNaslova?: boolean;
}) {
  return (
    <Stack gap="sm" className="clanek-pomoci">
      {!brezNaslova && (
        <Title order={1} size="h2">
          {clanek.naslov}
        </Title>
      )}
      {clanek.opis && (
        <Text c="dimmed" size="md">
          {clanek.opis}
        </Text>
      )}
      {clanek.vsebina.map((b, i) => (
        <Fragment key={i}>{blok(b, osnova)}</Fragment>
      ))}
    </Stack>
  );
}

function blok(b: Blok, osnova: string): ReactNode {
  switch (b.t) {
    case 'naslov':
      return (
        <Title order={b.raven} size={b.raven === 2 ? 'h3' : 'h4'} mt="sm">
          {vrstica(b.v, osnova)}
        </Title>
      );
    case 'odstavek':
      return <Text size="sm">{vrstica(b.v, osnova)}</Text>;
    case 'seznam':
      return (
        <List type={b.urejen ? 'ordered' : 'unordered'} size="sm" spacing={4} withPadding>
          {b.postavke.map((p, i) => (
            <List.Item key={i}>{vrstica(p, osnova)}</List.Item>
          ))}
        </List>
      );
    case 'slika':
      return (
        <figure style={{ margin: 0 }}>
          <Image
            src={SLIKE_POMOCI + b.datoteka}
            alt={b.opis}
            radius="md"
            w="auto"
            maw="100%"
            // naravna velikost posnetka (pogovorna okna se ne raztegnejo), na ozkem zaslonu pomanjšano
            style={{ border: `1px solid ${barve.obroba}`, display: 'block' }}
          />
          {b.opis && (
            <Text component="figcaption" size="xs" c="dimmed" mt={4}>
              {b.opis}
            </Text>
          )}
        </figure>
      );
    case 'opomba':
      return (
        <Alert
          variant="light"
          color={b.vrsta === 'pozor' ? 'yellow' : 'blue'}
          icon={b.vrsta === 'pozor' ? <IconAlertTriangle size={18} /> : <IconBulb size={18} />}
        >
          <Text size="sm">{vrstica(b.v, osnova)}</Text>
        </Alert>
      );
  }
}

function vrstica(v: Vrstica[], osnova: string): ReactNode {
  return v.map((x, i) => {
    switch (x.t) {
      case 'besedilo':
        return <Fragment key={i}>{x.v}</Fragment>;
      case 'krepko':
        return <b key={i}>{vrstica(x.v, osnova)}</b>;
      case 'koda':
        return <Code key={i}>{x.v}</Code>;
      case 'povezava':
        if (/^https?:/.test(x.cilj) || x.cilj.startsWith('mailto:')) {
          return (
            <Anchor key={i} href={x.cilj} target="_blank" rel="noopener noreferrer">
              {vrstica(x.v, osnova)}
            </Anchor>
          );
        }
        return (
          <Anchor key={i} component={Link} to={x.cilj.startsWith('clanek:') ? `${osnova}/${x.cilj.slice(7)}` : x.cilj}>
            {vrstica(x.v, osnova)}
          </Anchor>
        );
    }
  });
}
